// lib/http.ts
type Json = Record<string, unknown> | unknown[] | null

const API_BASE = (process.env.REACT_APP_API_BASE || '').trim()
if (!API_BASE) {
  throw new Error('REACT_APP_API_BASE is not set')
}

const STATIC_TOKEN = (process.env.REACT_APP_API_TOKEN || '').trim()

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
) {
  const url = new URL(`${API_BASE}${path}`)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

async function parseJsonSafely(res: Response) {
  if (res.status === 204) return null
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

type RequestOpts = {
  headers?: Record<string, string>
  params?: Record<string, string | number | boolean | undefined>
  body?: Json | BodyInit
  signal?: AbortSignal
  bearerToken?: string
  timeoutMs?: number
  json?: boolean
}

class AbortControllerComposite {
  private controller = new AbortController()
  constructor(signals: AbortSignal[]) {
    signals.forEach(s =>
      s.addEventListener('abort', () => this.controller.abort(), { once: true })
    )
  }
  get signal() {
    return this.controller.signal
  }
}

export async function request<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  opts: RequestOpts = {}
): Promise<T> {
  const {
    headers = {},
    params,
    body,
    signal,
    bearerToken,
    timeoutMs = 60_000, // give heavier procs room
    json = true
  } = opts

  const outer = new AbortController()
  const timer = setTimeout(() => outer.abort(), timeoutMs)
  const compositeSignal = signal
    ? new AbortControllerComposite([signal, outer.signal]).signal
    : outer.signal

  const token = (bearerToken || STATIC_TOKEN).trim()
  const finalHeaders: Record<string, string> = {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers
  }

  const url = buildUrl(path, params)

  if (process.env.NODE_ENV !== 'production') {
    const safeHeaders = {
      ...finalHeaders,
      Authorization: token ? 'Bearer ***' : ''
    }
  }

  try {
    const res = await fetch(url, {
      method,
      headers: finalHeaders,
      body:
        json && body && !(body instanceof FormData)
          ? JSON.stringify(body)
          : (body as BodyInit | null),
      signal: compositeSignal
      // credentials intentionally omitted (CORS wildcard)
    })

    const data = await parseJsonSafely(res)
    if (!res.ok) {
      const message =
        typeof data === 'string'
          ? data
          : (data as any)?.detail || res.statusText || `HTTP ${res.status}`
      throw new ApiError(message, res.status, data)
    }
    return data as T
  } finally {
    clearTimeout(timer)
  }
}

export const get = <T = unknown>(
  path: string,
  opts?: Omit<RequestOpts, 'body'>
) => request<T>('GET', path, opts)

export const post = <T = unknown>(
  path: string,
  body?: Json,
  opts?: Omit<RequestOpts, 'body'>
) => request<T>('POST', path, { ...opts, body })

export const put = <T = unknown>(
  path: string,
  body?: Json,
  opts?: Omit<RequestOpts, 'body'>
) => request<T>('PUT', path, { ...opts, body })

export const patch = <T = unknown>(
  path: string,
  body?: Json,
  opts?: Omit<RequestOpts, 'body'>
) => request<T>('PATCH', path, { ...opts, body })

export const del = <T = unknown>(
  path: string,
  opts?: Omit<RequestOpts, 'body'>
) => request<T>('DELETE', path, opts)
